import { useDrag } from '@use-gesture/react';
import * as React from 'react';
import { ENGAGE_THRESHOLD } from './constants';
import { useSwipeConfig } from './useSwipeConfig';
import { getElasticOffset, getMaxSwipeOffset, isSwipeCommitted } from './utils';
import { TUseMessageSwipeParams } from './types';

/**
 * Свайп сообщения для реплая. Жест распознаёт useDrag из @use-gesture/react:
 * он сам обрабатывает pointer-события, порог активации и конфликт со скроллом
 * (ось определяется как «первая ось, пересёкшая ENGAGE_THRESHOLD», что отсекает
 * вертикальные жесты).
 */
export const useMessageSwipe = ({ disabled, messageRef, onSwipe }: TUseMessageSwipeParams) => {
  const [dragOffset, setDragOffset] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);

  const { maxSwipeRatio, commitRatio } = useSwipeConfig();

  const isSwipeStartedRef = React.useRef(false);
  const maxSwipeOffsetRef = React.useRef(0);

  const bind = useDrag<PointerEvent>(
    ({ event, movement: [dx], first: isGestureStart, last: isGestureEnd, cancel }) => {
      const messageElement = messageRef.current;
      const isTouchPointerEvent = event.pointerType === 'touch';
      const isRightSwipe = dx >= 0;

      if (!isTouchPointerEvent || !messageElement || (isGestureStart && isRightSwipe)) {
        cancel();

        return;
      }

      if (isGestureStart) {
        maxSwipeOffsetRef.current = getMaxSwipeOffset(messageElement, maxSwipeRatio);
        isSwipeStartedRef.current = true;
        setIsSwiping(true);
      }

      const nextOffset = getElasticOffset(dx, maxSwipeOffsetRef.current);
      setDragOffset(nextOffset);

      if (isGestureEnd) {
        if (isSwipeCommitted({ offset: nextOffset, messageElement, commitRatio })) {
          onSwipe();
        }

        maxSwipeOffsetRef.current = 0;
        setIsSwiping(false);
        setDragOffset(0);
      }
    },
    {
      enabled: !disabled,
      axis: 'x',
      axisThreshold: { touch: ENGAGE_THRESHOLD },
      threshold: ENGAGE_THRESHOLD,
      pointer: { capture: false, keys: false },
    },
  );

  const handleClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    // Клик, пришедший сразу после свайпа, подавляем — жест не должен активировать
    // ссылки и кнопки, попавшие под палец
    if (!isSwipeStartedRef.current) {
      return;
    }

    isSwipeStartedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    offset: dragOffset,
    isSwiping,
    onClick: handleClick,
    ...bind(),
  };
};
